import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  InputAdornment
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import ServiceHistoryList from './ServiceHistoryList';
import ServiceHistoryDataService from '../../services/serviceHistory.service';
import ServiceDataService from '../../services/service.service';
import ClientService from '../../services/client.service';
import { getCurrencySymbolSync } from '../../utils/currencyUtils';
import { useTranslation } from '../../hooks/useTranslation';
const ServiceHistory = () => {
  const [currentView, setCurrentView] = useState('list');
  const [editingHistory, setEditingHistory] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation();
  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    service_id: '',
    costo: '',
    fecha: new Date().toISOString().split('T')[0] // Today's date
  });
  
  // Dropdown options
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    if (modalOpen) {
      loadDropdownData();
    }
  }, [modalOpen]);

  const loadDropdownData = async () => {
    try {
      setLoading(true);
      const [clientsResponse, servicesResponse] = await Promise.all([
        ClientService.getClients(),
        ServiceDataService.getAll()
      ]);

      if (clientsResponse.data.success) {
        setClients(clientsResponse.data.data || []);
      } else {
        setClients(clientsResponse.data?.data || []);
      }

      if (servicesResponse.data.success) {
        setServices(servicesResponse.data.data || []);
      } else {
        setServices(servicesResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      setError('Failed to load clients and services');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHistory = () => {
    setEditingHistory(null);
    setFormData({
      client_id: '',
      service_id: '',
      costo: '',
      fecha: new Date().toISOString().split('T')[0]
    });
    setError('');
    setSuccess(false);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setFormData({
      client_id: '',
      service_id: '',
      costo: '',
      fecha: new Date().toISOString().split('T')[0]
    });
    setError('');
    setSuccess(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.client_id) {
      setError('Please select a client');
      return;
    }
    if (!formData.service_id) {
      setError('Please select a service');
      return;
    }
    if (!formData.costo || parseFloat(formData.costo) <= 0) {
      setError('Please enter a valid price');
      return;
    }
    if (!formData.fecha) {
      setError('Please select a date');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const serviceData = {
        client_id: parseInt(formData.client_id),
        service_id: parseInt(formData.service_id),
        costo: parseFloat(formData.costo),
        fecha: formData.fecha
      };

      await ServiceHistoryDataService.create(serviceData);
      setSuccess(true);
      
      // Reload the list after a short delay
      setTimeout(() => {
        handleClose();
        // Trigger a page refresh to show the new record
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error creating service history:', error);
      setError(error.response?.data?.message || 'Failed to create service history. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const renderCurrentView = () => {
    switch (currentView) {
      case 'list':
        return (
          <ServiceHistoryList
            onCreate={handleCreateHistory}
          />
        );
      default:
        return (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        );
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 3
        }}
      >
        <Container maxWidth="xl">
          {renderCurrentView()}
        </Container>
      </Box>

      {/* Create Service History Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span> {t ('serviceHistory.createHService')}</span>
            <Button
              onClick={handleClose}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t ('serviceHistory.msgcreateHService')}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Client</InputLabel>
                <Select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  label="Client"
                  disabled={loading}
                >
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Service</InputLabel>
                <Select
                  name="service_id"
                  value={formData.service_id}
                  onChange={handleChange}
                  label="Service"
                  disabled={loading}
                >
                  {services.map((service) => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="costo"
                label= {t ('common.price')}
                type="number"
                value={formData.costo}
                onChange={handleChange}
                fullWidth
                required
                disabled={loading}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">{getCurrencySymbolSync()}</InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="fecha"
                label= {t ('common.date')}
                type="date"
                value={formData.fecha}
                onChange={handleChange}
                fullWidth
                required
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            variant="outlined"
          >
          {t ('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            {loading ? 'Creating...' :t ('serviceHistory.createRecord')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServiceHistory;
